import Link from 'next/link'
import type { LucideIcon } from 'lucide-react'

import { Button, type EncuentroButtonVariant } from '@/components/ui/button'

type PageActionBase = {
  /** Verb in the infinitive: "Invitar", "Publicar un empleo", "Volver a intentar". */
  label: string
  /** Lucide icon before the label. */
  icon?: LucideIcon
  /**
   * Accessible name when the label alone is ambiguous ("Escribirle a Martín
   * Ferreyra"). It must start with the visible label.
   */
  accessibleName?: string
}

type PageLinkAction = PageActionBase & {
  href: string
  onClick?: never
  disabled?: never
}

type PageButtonAction = PageActionBase & {
  onClick: () => void
  href?: never
  /** While the action runs. */
  disabled?: boolean
}

/**
 * An action of a page-level component (PageHeader, EmptyState), described as
 * data instead of a rendered button. The component picks the variant, so a
 * caller cannot slip a second primary into a header: the one-primary rule is
 * in the prop types, not in a review.
 *
 * `onClick` needs a Client Component caller (functions do not cross the
 * server boundary); a Server Component passes `href`.
 */
export type PageAction = PageLinkAction | PageButtonAction

type PageActionButtonProps = {
  action: PageAction
  variant: EncuentroButtonVariant
  size?: 'md' | 'sm'
  block?: boolean
  className?: string
}

/** Renders a `PageAction` as the Encuentro `Button`: a link or a plain button. */
export function PageActionButton({
  action,
  variant,
  size,
  block,
  className,
}: PageActionButtonProps) {
  const Icon = action.icon
  const content = (
    <>
      {Icon && <Icon aria-hidden />}
      {action.label}
    </>
  )

  if (action.href !== undefined) {
    return (
      <Button
        look="encuentro"
        variant={variant}
        size={size}
        block={block}
        className={className}
        asChild
      >
        <Link href={action.href} aria-label={action.accessibleName}>
          {content}
        </Link>
      </Button>
    )
  }

  return (
    <Button
      look="encuentro"
      variant={variant}
      size={size}
      block={block}
      className={className}
      onClick={action.onClick}
      disabled={action.disabled}
      aria-label={action.accessibleName}
    >
      {content}
    </Button>
  )
}
